import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2024 semantics GmbH
//  Date      : $Date: 2026-02-18 19:10:56 +0100 (Wed, 18 Feb 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $Author: d.rothe
//  Revision  : $Rev: 160820 $
//  Purpose   : frontend gndo components
// =============================================================================
// ---Third-party imports
import React from 'react';
import clsx from 'clsx';
// ---Framework imports
import { get, size } from 'ldash';
import { Actions, Sel } from 'uix/registries';
import log from 'uix/logging';
import { DebugFeature, Link } from 'uix/components';
import { useApi, usePartContext } from 'uix/hooks';
import { formatDate } from 'uix/utils/date';
// ---Local imports
import { Titles } from './titles';
// ---Globals
const render_string_list = (gndo, name, sep = ' ; ', max = 100)=>{
    let data = gndo[name];
    if (size(data)) {
        return [
            `gndo_${name}`,
            data.slice(0, max).join(' ; ')
        ];
    }
};
const render_label_list = (gndo, name, sep = ' ; ')=>{
    let data = gndo[name];
    if (size(data)) {
        return [
            `gndo_${name}`,
            data.map((e)=>e.label).join(' ; ')
        ];
    }
};
const format_life_data = (gndo, i18n)=>{
    let life_data = '';
    if (gndo.dateOfBirth) {
        life_data += `${i18n.abbrBorn} ${formatDate(gndo.dateOfBirth[0])}`;
        if (gndo.placeOfBirth) {
            life_data += ` ${i18n.inLoc} ${gndo.placeOfBirth[0].label}`;
        }
    }
    if (gndo.dateOfDeath) {
        life_data += `; ${i18n.abbrDied} ${formatDate(gndo.dateOfDeath[0])}`;
        if (gndo.placeOfDeath) {
            life_data += ` ${i18n.inLoc} ${gndo.placeOfDeath[0].label}`;
        }
    }
    return life_data;
};
const build_country = (gndo)=>render_label_list(gndo, 'geographicAreaCode');
const build_professionOrOccupation = (gndo)=>render_label_list(gndo, 'professionOrOccupation');
const build_biographicalOrHistoricalInformation = (gndo)=>render_string_list(gndo, 'biographicalOrHistoricalInformation');
const build_affiliation = (gndo)=>render_label_list(gndo, 'affiliation');
const build_variantName = (gndo)=>render_string_list(gndo, 'variantName', ' ; ', 6);
const build_placeOfBusiness = (gndo)=>render_label_list(gndo, 'placeOfBusiness');
const build_dateOfEstablishment = (gndo)=>render_string_list(gndo, 'dateOfEstablishment');
export const GndoInfo = ({ otid, className, style })=>{
    let { i18n, apiurl } = usePartContext();
    let { data } = useApi('gndo/getGndoDetails', {
        otid
    }, {
        autoreset: false,
        cached: true
    });
    let gndo = data?.gndo || {};
    let img = null;
    if (data?.depiction) {
        let { width, height } = data.depiction;
        let src = apiurl.href(`/image/depiction/${otid}`);
        img = /*#__PURE__*/ _jsx("img", {
            width: width,
            height: height,
            src: src,
            style: {
                width: '30%',
                height: 'auto',
                marginRight: '1em'
            }
        });
    }
    let info_line = (f)=>{
        let res = f(gndo, i18n);
        if (res) {
            let [i18n_ident, rendered] = res;
            return /*#__PURE__*/ _jsxs("div", {
                style: {
                    display: 'table',
                    width: '100%'
                },
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        style: {
                            display: 'table-cell',
                            width: '40%',
                            paddingRight: '0.5em'
                        },
                        children: [
                            /*#__PURE__*/ _jsx("b", {
                                children: i18n.span(i18n_ident)
                            }),
                            ":",
                            ' '
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        style: {
                            display: 'table-cell'
                        },
                        children: rendered
                    })
                ]
            });
        }
    };
    let styles = {
        display: 'flex',
        alignItems: 'flex-start',
        ...style
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: className,
        style: styles,
        children: [
            img,
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        children: /*#__PURE__*/ _jsx(Link, {
                            id: otid,
                            children: gndo.preferredName
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        children: format_life_data(gndo, i18n)
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            info_line(build_country),
                            info_line(build_professionOrOccupation),
                            info_line(build_biographicalOrHistoricalInformation),
                            info_line(build_affiliation),
                            info_line(build_variantName),
                            info_line(build_placeOfBusiness),
                            info_line(build_dateOfEstablishment)
                        ]
                    })
                ]
            })
        ]
    });
};
export const GndoTitles = ({ otid, thumbWidth, style })=>{
    let { data } = useApi('gndo/getGndoMetadataList', {
        otid
    }, {
        autoreset: false
    });
    let otids = data?.otids || [];
    let titles = otids.map((otid)=>({
            otid
        }));
    return /*#__PURE__*/ _jsx(Titles, {
        style: style,
        titles: titles,
        thumbWidth: thumbWidth,
        link: true
    });
};
